/**
 * Pure predicates for the hospitality venue-home real-estate additions
 * (Issy's Love Inn rework 2026-09-07). Extracted from `web/src/pages/venue.tsx`
 * so the visibility gates are unit-testable in isolation, matching the
 * router-decisions.ts pattern that closed the PR #184 gap.
 *
 * Rules encoded here:
 *   - Stats block is member-only, non-studio, requires a signed-in user.
 *   - Loyalty card only if the venue has loyalty turned on; when off the
 *     ticket card expands to full width.
 *   - Latest news renders whenever a bulletin exists AND the venue isn't a
 *     studio (studios use their own layout).
 *   - "This week" = events starting in the next 7 days; falls back to the
 *     first N upcoming when nothing lands in the week so the section never
 *     collapses to empty on a quiet week.
 */

type HomeEvent = { id: string; date: string | Date };

export function shouldShowStatsBlock(input: {
  hasActiveAccess: boolean;
  isStudio: boolean;
  hasUser: boolean;
}): boolean {
  return input.hasActiveAccess && !input.isStudio && input.hasUser;
}

export function shouldShowLoyaltyCard(input: {
  statsVisible: boolean;
  loyaltyEnabled: boolean;
}): boolean {
  return input.statsVisible && input.loyaltyEnabled;
}

export function shouldShowLatestNews(input: {
  isStudio: boolean;
  hasBulletin: boolean;
}): boolean {
  return !input.isStudio && input.hasBulletin;
}

// "Coming soon" teaser card on the venue home (Issy 2026-09-09 Love Inn
// polish). Cross-tenant peer-review fix 2026-09-09: was hardcoded on
// every non-studio venue, which surfaced Love Inn's product roadmap to
// Prospect/Toka/etc. Now gated on venue-configurable appConfig, so it
// only renders where a venue has explicitly opted in via
// appConfig.homeComingSoon.enabled = true. Copy is also configurable
// (title, body) with a benign default for the opt-in case.
type HomeComingSoonConfig = {
  enabled?: boolean;
  title?: string;
  body?: string;
};

export function shouldShowComingSoon(input: {
  isStudio: boolean;
  config: HomeComingSoonConfig | null | undefined;
}): boolean {
  return !input.isStudio && input.config?.enabled === true;
}

export function pickThisWeekEvents<E extends HomeEvent>(input: {
  events: E[];
  now: Date;
  fallbackCount?: number;
}): { list: E[]; hasMore: boolean } {
  const fallback = input.fallbackCount ?? 4;
  const cutoff = new Date(input.now.getTime() + 7 * 24 * 60 * 60_000);
  const upcoming = input.events.filter((e) => new Date(e.date) >= input.now);
  const thisWeek = upcoming.filter((e) => new Date(e.date) <= cutoff);
  const list = thisWeek.length > 0 ? thisWeek : upcoming.slice(0, fallback);
  const hasMore = upcoming.length > list.length;
  return { list, hasMore };
}

// Redemption eligibility: strict ACTIVE only. Deliberately NARROWER than
// `hasActiveAccess` (which includes CANCELED-in-period for read-side venue
// access), because the redeem API rejects anything but ACTIVE and we don't
// want the featured perk card to render a button that will 403. Peer review
// 2026-09-09 caught the mismatch (dc077dcb).
export function isRedemptionEligible(status: string | null | undefined): boolean {
  return status === "ACTIVE";
}

// Redeemables surfaced on the venue home for members. Selection rules,
// same as pickFeaturedPerk but returns the full list (Fenton 2026-09-09,
// "redeem from the main UI not a click-through"):
//   1. Only benefits on tiers the user's tier can access (tier.sortOrder
//      <= their tier sortOrder), so a Community member never sees a
//      Regulars-only perk dangled at them.
//   2. Only benefits with isRedeemable: true (passive benefits like "10%
//      off tickets" have no home surface).
//   3. Welcome rewards first (one-off perks that decay in value the
//      longer they're unclaimed), then everything else.
//   4. Deterministic tiebreak on id so re-renders don't reorder.
type PerkBenefit = {
  id: string;
  isRedeemable?: boolean;
  isWelcomeReward?: boolean;
  description?: string | null;
  tier: { sortOrder: number };
};

export function pickHomeRedeemables<B extends PerkBenefit>(input: {
  benefits: B[];
  memberTierSortOrder: number | null;
}): B[] {
  if (input.memberTierSortOrder == null) return [];
  const eligible = input.benefits.filter(
    (b) => b.isRedeemable === true && b.tier.sortOrder <= input.memberTierSortOrder!,
  );
  return [...eligible].sort((a, b) => {
    const aw = a.isWelcomeReward ? 0 : 1;
    const bw = b.isWelcomeReward ? 0 : 1;
    if (aw !== bw) return aw - bw;
    return a.id.localeCompare(b.id);
  });
}
